import { useState } from "react";
import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { Badge } from "../../../ui/badge";
import { Skeleton } from "../../../ui/skeleton";
import { GroupedBarChart } from "../../../components/PortfolioPerformance/charts/grouped-bar-chart";
import { TrendLineChart } from "../../../components/PortfolioPerformance/charts/trend-line-chart";
import { FilterBar } from "../../../components/PortfolioPerformance/filter-bar";
import { MultiSelectFilter } from "../../../components/PortfolioPerformance/multi-select-filter";
import { cohortColor } from "../lib/api/lifetime-loss-projections";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
import { useLgd, useLgdLowess, type LgdGroup } from "../lib/api/lgd";

const ALL_INDUSTRIES = "All Industries";

/** Asset | Borrower switch on the two smoothing charts: it changes the industries they plot and the Industry filter. */
function GroupToggle({ value, onChange }: { value: LgdGroup; onChange: (next: LgdGroup) => void }) {
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(next) => next && onChange(next as LgdGroup)}
      className="border border-border bg-secondary"
      aria-label="Industry type"
    >
      <ToggleGroupItem value="asset" className="h-9 px-3 text-xs">
        Asset Industry
      </ToggleGroupItem>
      <ToggleGroupItem value="borrower" className="h-9 px-3 text-xs">
        Borrower Industry
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

export default function LgdPage() {
  const { data, isPending, isError, error } = useLgd();

  // Lowess charts: industry is a server-side filter; contract terms are a multi-select where
  // null means "every term" (so new terms in the data are included without being ticked).
  const [industry, setIndustry] = useState<string>(ALL_INDUSTRIES);
  // Asset or borrower industries: the toggle on the two charts. Switching starts again from "All Industries", since
  // an asset industry means nothing in the borrower list (and the other way round).
  const [group, setGroup] = useState<LgdGroup>("asset");
  const changeGroup = (next: LgdGroup) => {
    setGroup(next);
    setIndustry(ALL_INDUSTRIES);
  };
  const [selectedTerms, setSelectedTerms] = useState<Set<string> | null>(null);
  const lowessAll = useLgdLowess(undefined, undefined, group);
  const termRanges = lowessAll.data?.termRanges ?? [];
  const termsInUse = selectedTerms ? termRanges.filter((t) => selectedTerms.has(t)) : termRanges;
  const noTerms = selectedTerms !== null && termsInUse.length === 0;
  // Always enabled: the Actual vs. Smoothened chart is the all-terms line, so it does not
  // depend on the term selection (with no terms ticked, only the by-term chart goes empty).
  const lowess = useLgdLowess(
    industry === ALL_INDUSTRIES ? undefined : industry,
    selectedTerms ? termsInUse : undefined,
    group,
  );

  return (
    <div>
      <PageHeader
        title="Loss Given Default (LGD)"
        subtitle="The backbone driver of Expected Loss: recovery-based, smoothed, by asset industry and borrower industry."
      />

      {isError ? (
        <div
          role="alert"
          className="mt-8 border border-decline/50 bg-decline/10 p-5 text-sm text-foreground"
        >
          <p className="font-semibold">Could not load LGD data.</p>
          <p className="mt-1 text-muted-foreground">
            {error instanceof Error ? error.message : "Unknown failure."}
          </p>
        </div>
      ) : isPending ? (
        <div className="mt-8 space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : data === null ? (
        <div className="mt-8 border border-border bg-card p-5 text-sm text-muted-foreground">
          Not Available Yet.
        </div>
      ) : (
        <>
          <div className="mt-8">
            <Badge variant="outline">
              {data.isStatic
                ? `Static as of ${data.asOf} — pipeline in progress`
                : `As of ${data.asOf} · refreshed monthly`}
            </Badge>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Portfolio LGD (Loan to Life Loss)
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {data.portfolioLgd}%
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Recovery rate: {data.recoveryRate}% · 100 − Portfolio LGD
              </p>
            </div>
            <div className="border border-border bg-card p-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Industries with a Smoothed Curve
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {data.assetIndustriesModeled} / {data.borrowerIndustriesModeled}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                asset / borrower industries with enough defaults
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-end gap-4">
            <FilterBar
              filters={[
                {
                  id: "industry",
                  label: group === "asset" ? "Asset Industry" : "Borrower Industry",
                  value: industry,
                  options: [ALL_INDUSTRIES, ...(lowessAll.data?.industries ?? [])],
                  onChange: setIndustry,
                },
              ]}
            />
            <MultiSelectFilter
              label="Contract Term"
              options={termRanges}
              selected={new Set(termsInUse)}
              onChange={(next) =>
                setSelectedTerms(next.size === termRanges.length ? null : next)
              }
              allLabel="All Terms"
              noneLabel="No Terms"
              pluralNoun="Terms"
            />
          </div>

          <Panel
            className="mt-6"
            title="LGD: Actual vs. Smoothened"
            description="Actual LGD (net loss ÷ gross loss) and its Lowess smoothing, by months to default, all contract terms"
            right={<GroupToggle value={group} onChange={changeGroup} />}
          >
            {lowess.isError ? (
              <p className="text-sm text-destructive">Could not load the Lowess smoothing data.</p>
            ) : !lowess.data ? (
              <Skeleton className="h-64 w-full" />
            ) : lowess.data.smoothing.length === 0 ? (
              <p className="text-sm text-muted-foreground">Not Available Yet.</p>
            ) : (
              <TrendLineChart
                data={lowess.data.smoothing}
                series={[
                  { dataKey: "actualLgd", name: "Actual LGD", color: "hsl(var(--chart-2))" },
                  { dataKey: "smoothedLgd", name: "Smoothened LGD", color: "var(--amber)" },
                ]}
                suffix="%"
                height={280}
              />
            )}
          </Panel>

          <Panel
            className="mt-6"
            title="LGD by Contract Term"
            description="Smoothed LGD (3-month rolling mean) by months to default, one line per contract term"
            right={<GroupToggle value={group} onChange={changeGroup} />}
          >
            {noTerms ? (
              <p className="text-sm text-muted-foreground">Select at least one contract term.</p>
            ) : lowess.isError ? (
              <p className="text-sm text-destructive">Could not load the Lowess smoothing data.</p>
            ) : !lowess.data ? (
              <Skeleton className="h-64 w-full" />
            ) : lowess.data.byTerm.length === 0 ? (
              <p className="text-sm text-muted-foreground">Not Available Yet.</p>
            ) : (
              <TrendLineChart
                data={lowess.data.byTerm}
                series={termsInUse.map((term) => ({
                  dataKey: term,
                  name: term,
                  color: cohortColor(termRanges.indexOf(term)),
                }))}
                suffix="%"
                height={280}
              />
            )}
          </Panel>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <Panel
              title="LGD by Asset Industry"
              description="Net loss ÷ gross loss, the 6 industries with the largest gross loss"
            >
              <GroupedBarChart
                data={data.byAssetIndustry}
                categoryKey="name"
                series={[{ dataKey: "lgd", name: "LGD %", color: "var(--decline)" }]}
                suffix="%"
                layout="horizontal-bars"
                categoryWidth={140}
                height={280}
              />
            </Panel>

            <Panel
              title="LGD by Borrower Industry"
              description="Net loss ÷ gross loss, the 6 industries with the largest gross loss"
            >
              <GroupedBarChart
                data={data.byBorrowerIndustry}
                categoryKey="name"
                series={[{ dataKey: "lgd", name: "LGD %", color: "hsl(var(--chart-2))" }]}
                suffix="%"
                layout="horizontal-bars"
                categoryWidth={140}
                height={280}
              />
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
